import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Link,
  Chip,
} from '@mui/material';
import {
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  VerifiedUser as VerifiedUserIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DataTable from '../../components/ui/DataTable';
import { LoadingPage } from '../../components/ui/LoadingSpinner';
import { formatDate } from '../../lib/utils';
import apiClient from '../../lib/api';
import { normalizeListPagination } from '../../lib/normalizeListPagination';
import { toast } from 'react-hot-toast';

const FuneralCompaniesAdminPage = () => {
  const navigate = useNavigate();
  const [funeralCompanies, setFuneralCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stripeAccounts, setStripeAccounts] = useState({}); // Map of companyId -> account details
  const [companyTypeFilter, setCompanyTypeFilter] = useState('all'); // Default to all so admins see every company

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });

  // Load funeral companies data
  const loadFuneralCompanies = async (searchParams) => {
    try {
      setIsLoading(true);
      
      const params = {
        page: (searchParams?.page ?? 1),
        limit: searchParams?.rowsPerPage ?? 20,
      };

      // Add search parameter
      if (searchParams?.searchTerm) {
        params.search = searchParams.searchTerm;
      }

      // Add company type filter from filterValues (or use state default). Omit when 'all' so admins see every company.
      const filterCompanyType = searchParams?.filterValues?.companyType ?? companyTypeFilter;
      if (filterCompanyType && filterCompanyType !== 'all') {
        params.companyType = filterCompanyType;
        setCompanyTypeFilter(filterCompanyType);
      } else {
        setCompanyTypeFilter('all');
      }
      // When 'all', no companyType param is sent — backend returns all companies
      
      console.log('Loading companies with params:', params);
      
      const response = await apiClient.getFuneralCompanies(params);
      
      console.log('API Response:', response);
      console.log('Funeral companies data:', response.data);
      const companiesData = response.data || [];
      setFuneralCompanies(companiesData);
      
      // Fetch Stripe Connect account details for each company
      const accountsMap = {};
      await Promise.all(
        companiesData.map(async (company) => {
          if (company.stripeConnectAccount) {
            try {
              const accountResponse = await apiClient.getStripeConnectAccount(company.stripeConnectAccount);
              accountsMap[company.id] = accountResponse.data || accountResponse;
            } catch (error) {
              // Account might not be accessible, set to null
              accountsMap[company.id] = null;
            }
          }
        })
      );
      setStripeAccounts(accountsMap);
      
      setPagination(normalizeListPagination(response, { limit: 20 }));
    } catch (error) {
      console.error('Error loading funeral companies:', error);
      toast.error('Failed to load funeral companies');
      setFuneralCompanies([]);
      setPagination({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Initial load with default filter
    loadFuneralCompanies({ filterValues: { companyType: companyTypeFilter } });
  }, []); // Only run on mount

  // Separate effect to reload when filter changes
  useEffect(() => {
    if (companyTypeFilter) {
      loadFuneralCompanies({ filterValues: { companyType: companyTypeFilter } });
    }
  }, [companyTypeFilter]);

  const columns = [
    { 
      id: 'companyName', 
      label: 'Company Name', 
      sortable: true,
      render: (value, row) => (
        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
          {value || 'Unnamed Company'}
        </Typography>
      )
    },
    { 
      id: 'contactPersonName', 
      label: 'Contact Person', 
      sortable: true,
      render: (value, row) => (
        <Typography variant="body2">
          {value || '-'}
        </Typography>
      )
    },
    { 
      id: 'phone', 
      label: 'Phone', 
      sortable: true,
      render: (value, row) => (
        <Typography variant="body2">
          {value || '-'}
        </Typography>
      )
    },
    { 
      id: 'email', 
      label: 'Email', 
      sortable: true,
      render: (value, row) => (
        <Typography variant="body2">
          {value || '-'}
        </Typography>
      )
    },
    { 
      id: 'website', 
      label: 'Website', 
      sortable: true,
      render: (value, row) => {
        if (value) {
          return (
            <Link 
              href={value} 
              target="_blank" 
              rel="noopener noreferrer" 
              sx={{ 
                color: 'primary.main', 
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              {value}
            </Link>
          );
        }
        return (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        );
      }
    },
    { 
      id: 'createdAt', 
      label: 'Created', 
      sortable: true,
      render: (value, row) => (
        <Typography variant="body2" color="text.secondary">
          {value ? formatDate(value) : 'Unknown'}
        </Typography>
      )
    },
    {
      id: 'stripeConnect',
      label: 'Stripe Connect',
      sortable: false,
      render: (value, row) => {
        const account = stripeAccounts[row.id];
        const accountId = row.stripeConnectAccount;
        
        if (!accountId) {
          return (
            <Typography variant="body2" color="text.secondary">
              Not Connected
            </Typography>
          );
        }
        
        if (!account) {
          return (
            <Box>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {accountId.substring(0, 20)}...
              </Typography>
              <Chip label="Loading..." size="small" color="default" />
            </Box>
          );
        }
        
        const isVerified = account.details_submitted && account.charges_enabled && account.payouts_enabled;
        const status = isVerified ? 'Verified' : 'Pending';
        
        return (
          <Box>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', mb: 0.5 }}>
              {accountId}
            </Typography>
            <Chip 
              label={status} 
              size="small" 
              color={isVerified ? 'success' : 'warning'}
            />
          </Box>
        );
      },
    },
  ];

  const filters = [
    {
      key: 'companyType',
      label: 'Company Type',
      type: 'select',
      options: [
        { value: 'all', label: 'All' },
        { value: 'funeral', label: 'Funeral' },
        { value: 'designer', label: 'Designer' },
      ],
    },
    {
      key: 'search',
      label: 'Search',
      type: 'text',
      placeholder: 'Search by company name...'
    },
  ];

  // Handle search from DataTable
  const handleSearch = (searchParams) => {
    console.log('Search params:', searchParams);
    loadFuneralCompanies(searchParams);
  };

  const handleRowSelect = (company) => {
    navigate(`/companies/${company.id}`);
  };

  const handleAdd = () => {
    navigate('/companies/add');
  };

  // Remove the full-page loading state that causes unmounting
  // Instead, show loading state within the DataTable component

  return (
    <DashboardLayout title="Companies Management">
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
        Companies
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
          gap: 2 
        }}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4a8f 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <BusinessIcon sx={{ mr: 1, fontSize: 24 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Total Companies
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {pagination.total}
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ 
            background: 'linear-gradient(135deg, #d885e8 0%, #e04a5f 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckCircleIcon sx={{ mr: 1, fontSize: 24 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Active Companies
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {funeralCompanies.length}
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ 
            background: 'linear-gradient(135deg, #4a9ee8 0%, #00d8e0 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <VerifiedUserIcon sx={{ mr: 1, fontSize: 24 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  With Website
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {funeralCompanies.filter(c => c.website).length}
              </Typography>
            </CardContent>
          </Card>
          
          <Card sx={{ 
            background: 'linear-gradient(135deg, #3dd06f 0%, #32e0c0 100%)',
            color: 'white',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon sx={{ mr: 1, fontSize: 24 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  New This Month
                </Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                {funeralCompanies.filter(c => {
                    const created = new Date(c.createdAt);
                    const now = new Date();
                    return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
                  }).length}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Companies Table */}
      <Box sx={{ mt: 4 }}>         
        <DataTable
          data={funeralCompanies}
          title="Companies"
          onAdd={handleAdd}
          addButtonText={companyTypeFilter === 'designer' ? "Add Designer Company" : "Add Funeral Company"}
          columns={columns}
          filters={filters}
          onSelect={handleRowSelect}
          onSearch={handleSearch}
          totalPages={pagination.totalPages}
          currentPage={pagination.page}
          defaultRowsPerPage={20}
          loading={isLoading}
          onRowClick={(row) => navigate(`/companies/${row.id}`)}
          initialFilterValues={{ companyType: companyTypeFilter }}
        />
      </Box>
    </DashboardLayout>
  );
};

export default FuneralCompaniesAdminPage;
